import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { 
  ServerIcon,
  KeyIcon,
  PlusIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  SignalIcon,
  ClockIcon,
  CommandLineIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

function HL7ConnectorManager() {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newToken, setNewToken] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showTokenFormModal, setShowTokenFormModal] = useState(false);
  const [showCommandModal, setShowCommandModal] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [commandType, setCommandType] = useState('send_note');
  const [commandPayload, setCommandPayload] = useState('');
  const [commandResult, setCommandResult] = useState(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [tokenDescription, setTokenDescription] = useState('');
  const [showTokenValue, setShowTokenValue] = useState(false);
  const [selectedConnectorForToken, setSelectedConnectorForToken] = useState(null);

  // Fetch connectors on component mount
  useEffect(() => {
    fetchConnectors();
    // Set up polling to refresh connector status every 30 seconds
    const interval = setInterval(fetchConnectors, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchConnectors = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/hl7-connectors`);
      setConnectors(response.data.connectors || []);
      setError('');
    } catch (error) {
      console.error('Error fetching connectors:', error);
      setError('Failed to fetch HL7 connectors: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const generateTokenForConnector = async (connectorId) => {
    if (!tokenName.trim()) {
      setError('Token name is required');
      return;
    }

    try {
      setLoading(true);
      
      // Use different endpoints based on whether it's for a specific connector or a new one
      let response;
      if (connectorId === 'new-connector') {
        // Generate a general token that can be used by any connector
        response = await axios.post(`${API_URL}/api/hl7-connectors/generate-auth-token`, {
          name: tokenName.trim(),
          description: tokenDescription.trim()
        });
      } else {
        // Generate a token for a specific existing connector
        response = await axios.post(`${API_URL}/api/hl7-connectors/${connectorId}/generate-token`, {
          name: tokenName.trim(),
          description: tokenDescription.trim()
        });
      }
      
      console.log('Token generation response:', response.data);
      console.log('Token object:', response.data.token);
      console.log('Token value:', response.data.token?.token);
      
      // Check if we got a valid token
      if (!response.data.token?.token) {
        console.error('Token value is missing from response!');
        setError('Token was created but the value was not returned. Check server logs.');
        return;
      }
      
      setNewToken(response.data.token);
      setShowTokenModal(true);
      setShowTokenFormModal(false);
      setShowTokenValue(true); // Auto-show the token value on generation
      setTokenName('');
      setTokenDescription('');
      setSelectedConnectorForToken(null);
      setSuccess('New authentication token generated successfully!');
      setError('');
      fetchConnectors(); // Refresh to show updated connector with token
    } catch (error) {
      console.error('Error generating token:', error);
      setError('Failed to generate auth token: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const deleteToken = async (connectorId, tokenId) => {
    if (!window.confirm('Are you sure you want to delete this token? The connector will no longer be able to connect.')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API_URL}/api/hl7-connectors/tokens/${tokenId}`);
      setSuccess('Token deleted successfully');
      setError('');
      fetchConnectors(); // Refresh to show updated connector without token
    } catch (error) {
      console.error('Error deleting token:', error);
      setError('Failed to delete token: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };


  const disconnectConnector = async (instanceId) => {
    if (!window.confirm(`Are you sure you want to disconnect ${instanceId}?`)) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API_URL}/api/hl7-connectors/${instanceId}`);
      setSuccess(`Connector ${instanceId} disconnected successfully`);
      setError('');
      fetchConnectors(); // Refresh the list
    } catch (error) {
      console.error('Error disconnecting connector:', error);
      setError('Failed to disconnect connector: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const sendCommand = async () => {
    try {
      setLoading(true);
      let payload;
      
      try {
        payload = JSON.parse(commandPayload);
      } catch (parseError) {
        setError('Invalid JSON in command payload');
        setLoading(false);
        return;
      }

      const command = {
        type: commandType,
        payload: payload
      };

      const response = await axios.post(
        `${API_URL}/api/hl7-connectors/${selectedConnector.instanceId}/send-command`,
        { command }
      );
      
      setCommandResult(response.data);
      setSuccess(`Command sent to ${selectedConnector.instanceId} successfully!`);
      setError('');
    } catch (error) {
      console.error('Error sending command:', error);
      setError('Failed to send command: ' + (error.response?.data?.error || error.message));
      setCommandResult(null);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 2000);
  };

  const getStatusInfo = (connector) => {
    if (connector.instanceId.startsWith('unassigned-')) {
      return {
        color: 'text-yellow-600',
        icon: ExclamationTriangleIcon,
        text: 'Unassigned',
        bgColor: 'bg-yellow-100'
      };
    } else if (connector.connected) {
      return {
        color: 'text-green-600',
        icon: CheckCircleIcon,
        text: 'Online',
        bgColor: 'bg-green-100'
      };
    } else {
      return {
        color: 'text-red-600',
        icon: XCircleIcon,
        text: 'Offline',
        bgColor: 'bg-red-100'
      };
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      return format(new Date(timestamp), 'MMM dd, yyyy HH:mm:ss');
    } catch (error) {
      return timestamp;
    }
  };

  const getDefaultCommandPayload = (type) => {
    switch (type) {
      case 'send_note':
        return JSON.stringify({
          integrationId: 'epic-integration-001',
          note: {
            patientId: 'P123456',
            content: 'Test note from HL7 Connector Manager',
            author: 'System Administrator',
            timestamp: new Date().toISOString()
          }
        }, null, 2);
      case 'receive_data':
        return JSON.stringify({
          query: {
            type: 'patient_list',
            filters: {
              lastUpdate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
            }
          }
        }, null, 2);
      case 'status_check':
        return '{}';
      default:
        return '{}';
    }
  };

  useEffect(() => {
    if (commandType) {
      setCommandPayload(getDefaultCommandPayload(commandType));
    }
  }, [commandType]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">HL7 Connector Management</h1>
            <p className="mt-2 text-gray-600">
              Manage your distributed HL7 connector instances and authentication tokens
            </p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={fetchConnectors}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <ServerIcon className="h-4 w-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={() => {
                setSelectedConnectorForToken('new-connector');
                setTokenName('');
                setShowTokenFormModal(true);
              }}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <KeyIcon className="h-4 w-4 mr-2" />
              Create New Connector Token
            </button>
          </div>
        </div>
      </div>


      {/* Status Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Connectors Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            HL7 Connectors ({connectors.length})
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Manage your HL7 connector instances and their authentication tokens
          </p>
        </div>
        
        {loading && connectors.length === 0 ? (
          <div className="px-4 py-5 sm:px-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading connectors...</p>
          </div>
        ) : connectors.length === 0 ? (
          <div className="px-4 py-5 sm:px-6 text-center">
            <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No connectors</h3>
            <p className="mt-1 text-sm text-gray-500">
              No HL7 connector instances are currently connected.
            </p>
            <div className="mt-6">
              <p className="text-sm text-gray-600 mb-4">
                To get started, create an authentication token that your connector can use to connect.
              </p>
              <button
                onClick={() => {
                  setSelectedConnectorForToken('new-connector');
                  setTokenName('');
                  setShowTokenFormModal(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <KeyIcon className="h-4 w-4 mr-2" />
                Create Your First Connector Token
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Connector
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auth Token
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Environment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Activity
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {connectors.map((connector) => {
                  const statusInfo = getStatusInfo(connector);
                  const hasToken = connector.token !== null;
                  const isUnassigned = connector.instanceId.startsWith('unassigned-');
                  
                  return (
                    <tr key={connector.instanceId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <ServerIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {isUnassigned ? `Token: ${connector.token?.name || 'Unnamed'}` : connector.instanceId}
                            </div>
                            {connector.metadata?.hostname && (
                              <div className="text-sm text-gray-500">
                                {connector.metadata.hostname}
                              </div>
                            )}
                            {isUnassigned && (
                              <div className="text-xs text-gray-500">
                                Waiting for connector to use this token
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <statusInfo.icon className={`h-5 w-5 mr-2 ${statusInfo.color}`} />
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${statusInfo.bgColor} ${statusInfo.color}`}>
                            {statusInfo.text}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {hasToken ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {connector.token.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Used {connector.token.usageCount} times
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                            No Token
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          connector.metadata?.environment === 'production' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {connector.metadata?.environment || 'unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {formatTimestamp(connector.metadata?.lastHeartbeat || connector.token?.lastUsed)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {/* Token actions */}
                          {hasToken ? (
                            <>
                              <button
                                onClick={() => {
                                  setNewToken(connector.token);
                                  setShowTokenModal(true);
                                  setShowTokenValue(false);
                                }}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Token Info"
                              >
                                <InformationCircleIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => deleteToken(connector.instanceId, connector.token.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete Token"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedConnectorForToken(connector.instanceId);
                                setTokenName(isUnassigned ? '' : `${connector.instanceId} Token`);
                                setShowTokenFormModal(true);
                              }}
                              className="text-green-600 hover:text-green-900"
                              title="Generate Token"
                            >
                              <KeyIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Command button - only for connected, assigned connectors */}
                          {!isUnassigned && (
                            <button
                              onClick={() => {
                                setSelectedConnector(connector);
                                setShowCommandModal(true);
                                setCommandResult(null);
                              }}
                              disabled={!connector.connected}
                              className="text-blue-600 hover:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                              title={connector.connected ? 'Send Command' : 'Connector not connected'}
                            >
                              <CommandLineIcon className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Disconnect button - only for connected connectors */}
                          {!isUnassigned && connector.connected && (
                            <button
                              onClick={() => disconnectConnector(connector.instanceId)}
                              className="text-red-600 hover:text-red-900"
                              title="Disconnect"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Token Creation Form Modal */}
      {showTokenFormModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Create Token for {selectedConnectorForToken}
                </h3>
                <button
                  onClick={() => {
                    setShowTokenFormModal(false);
                    setTokenName('');
                    setTokenDescription('');
                    setSelectedConnectorForToken(null);
                    setError('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Token Name *
                  </label>
                  <input
                    type="text"
                    value={tokenName}
                    onChange={(e) => setTokenName(e.target.value)}
                    placeholder="e.g., Production Server, Test Environment"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={tokenDescription}
                    onChange={(e) => setTokenDescription(e.target.value)}
                    placeholder="Brief description of what this token is used for"
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowTokenFormModal(false);
                    setTokenName('');
                    setTokenDescription('');
                    setSelectedConnectorForToken(null);
                    setError('');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => generateTokenForConnector(selectedConnectorForToken)}
                  disabled={loading || !tokenName.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <KeyIcon className="h-4 w-4 mr-2" />
                  {loading ? 'Generating...' : 'Generate Token'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auth Token Display Modal */}
      {showTokenModal && newToken && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {newToken.token ? 'New Authentication Token' : 'Token Information'}
                </h3>
                <button
                  onClick={() => setShowTokenModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-900">Token Name: {newToken.name}</p>
                  {newToken.description && (
                    <p className="text-sm text-gray-600">{newToken.description}</p>
                  )}
                </div>
                
                {/* Show token value section only if we have the actual token */}
                {newToken.token ? (
                  <>
                    <p className="text-sm text-gray-600 mb-3">
                      {showTokenValue 
                        ? 'Copy this token value and use it in your HL7 connector configuration:'
                        : 'Click "Show Token" to reveal the token value:'
                      }
                    </p>
                    <div className={`p-3 rounded-md ${showTokenValue ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-100'}`}>
                      {showTokenValue ? (
                        <>
                          <code className="text-sm break-all block mb-2">{newToken.token}</code>
                          <div className="flex items-center text-xs text-yellow-700">
                            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                            Keep this token secure and never share it publicly
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center py-4">
                          <div className="text-gray-500 text-sm">Token hidden for security - click "Show Token" to reveal</div>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-800 font-medium">Token value not available</p>
                        <p className="text-xs text-amber-700 mt-1">
                          For security, the token value is only shown once when first generated. 
                          If you need a new token, delete this one and generate a new one.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="mt-3 text-xs text-gray-500">
                  <p>• Token ID: {newToken.id}</p>
                  <p>• Created: {formatTimestamp(newToken.createdAt)}</p>
                  {newToken.lastUsed && <p>• Last Used: {formatTimestamp(newToken.lastUsed)}</p>}
                  {newToken.usageCount !== undefined && <p>• Usage Count: {newToken.usageCount}</p>}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                {newToken.token && !showTokenValue ? (
                  <button
                    onClick={() => setShowTokenValue(true)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    Show Token
                  </button>
                ) : newToken.token && showTokenValue ? (
                  <>
                    <button
                      onClick={() => setShowTokenValue(false)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <EyeSlashIcon className="h-4 w-4 mr-2" />
                      Hide Token
                    </button>
                    <button
                      onClick={() => copyToClipboard(newToken.token)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                      {copiedToken ? 'Copied!' : 'Copy Token'}
                    </button>
                  </>
                ) : null}
                <button
                  onClick={() => {
                    setShowTokenModal(false);
                    setNewToken(null);
                    setShowTokenValue(false);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Command Modal */}
      {showCommandModal && selectedConnector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-3/4 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Send Command to {selectedConnector.instanceId}
                </h3>
                <button
                  onClick={() => setShowCommandModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Command Type
                  </label>
                  <select
                    value={commandType}
                    onChange={(e) => setCommandType(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="send_note">Send Note</option>
                    <option value="receive_data">Receive Data</option>
                    <option value="status_check">Status Check</option>
                  </select>
                  
                  <label className="block text-sm font-medium text-gray-700 mt-4 mb-2">
                    Command Payload (JSON)
                  </label>
                  <textarea
                    value={commandPayload}
                    onChange={(e) => setCommandPayload(e.target.value)}
                    rows={12}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                    placeholder="Enter JSON payload..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Command Result
                  </label>
                  {commandResult ? (
                    <div className="bg-gray-100 p-4 rounded-md h-80 overflow-auto">
                      <pre className="text-sm">
                        {JSON.stringify(commandResult, null, 2)}
                      </pre>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-4 rounded-md h-80 flex items-center justify-center">
                      <p className="text-gray-500 text-sm">
                        Command result will appear here after sending
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCommandModal(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={sendCommand}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <CommandLineIcon className="h-4 w-4 mr-2" />
                  {loading ? 'Sending...' : 'Send Command'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HL7ConnectorManager;